import PageContainer from '@/components/PageContainer';
import { Box, Card, TextField, Typography } from '@mui/material';
import { FC } from 'react';
import stepsFields from '@/pages/Test/stepFields.json';
import { useNavigate, useParams } from 'react-router-dom';

import { Clinic, ClinicAdmin, Doctor, Patient, Test } from '@/types/database';
import TestReviewForm, { Values as FormValues } from '@/pages/admin/ReviewTest/components/TestReviewForm';

import { useAuth } from '@/components/auth';
import useAsync from '@/hooks/useAsync';
import YesNo from '@/components/YesNo';
import ImgUpload from '@/components/ImgUpload';
import { fetchTestData, submitReview } from './services/ReviewTest.api';

const ReviewTest: FC = () => {
  const { id: testId } = useParams<{ testId: string }>();
  const { user } = useAuth();

  const navigate = useNavigate();

  const { loading, data } = useAsync(() => fetchTestData(testId as string, user as Doctor), {
    inmediate: !!user && !!testId,
  });

  const test = data?.test as Test;
  const clinicAdmin = data?.clinicAdmin as ClinicAdmin;
  const patient = data?.patient as Patient;
  const clinic = data?.clinic as Clinic;

  const answer = test?.answer || {};

  const submitAction = async ({ action, notes }: FormValues) => {
    await submitReview(action, { clinicAdmin, doctor: user as Doctor, patient, clinic, test }, notes);

    navigate(-1);
  };

  return (
    <>
      <PageContainer loading={loading} back="up" title="reviewTestTitle" titleProps={{ mb: 2 }} goback>
        <Box mt={2} display="flex" flexDirection="column" gap={4} mb={25}>
          {stepsFields.map((step, index) => {
            const error = hasStepError(step, answer);
            return (
              <Card
                key={index}
                sx={{
                  padding: 2,
                  color: error ? 'red' : null,
                  borderColor: error ? 'red' : null,
                  borderWidth: 1,
                  borderStyle: 'solid',
                }}
              >
                {step.fields.map((field, fieldIndex) => {
                  if (field.options?.skipReview) return null;
                  const { component: Component, overrideProps } = COMPONENT_MAP[field.type] || COMPONENT_MAP['default'];

                  const value = answer[field.name] || '';

                  overrideProps.value = value;

                  return <Component error={error} key={fieldIndex} {...field.options} {...overrideProps} disabled />;
                })}
              </Card>
            );
          })}
        </Box>
      </PageContainer>
      <PageContainer
        maxWidth="sm"
        sx={{
          position: 'fixed',
          borderTop: '1px solid gray',
          bottom: 0,
          padding: 2,
          backgroundColor: 'white',
        }}
      >
        {test && (
          <TestReviewForm values={{ notes: test?.notes }} onSubmit={(values: FormValues) => submitAction(values)} />
        )}
      </PageContainer>
    </>
  );
};

export default ReviewTest;

const COMPONENT_MAP: Record<Field['type'], { component: FC<any>; overrideProps: any }> = {
  'text-input': {
    component: TextField,
    overrideProps: { size: 'small', fullWidth: true },
  },
  'yes/no': { component: YesNo, overrideProps: {} },
  'image-upload': { component: ImgUpload, overrideProps: {} },
  'text-field': { component: TextField, overrideProps: {} },

  img: { component: ImgUpload, overrideProps: {} },
  text: { component: Typography, overrideProps: { my: 1 } },
  default: { component: Typography, overrideProps: {} },
};

const hasStepError = (step: any, answer: any): boolean => {
  return step.fields.some((field: any) => {
    const value = answer[field.name];
    if (field.options?.required && !value) {
      return true;
    }
    if (field.type === 'yes/no' && !['yes', 'no'].includes(value)) {
      return true;
    }

    if (field.type === 'yes/no' && field.options?.hasError && field.options.hasError === value) {
      return true;
    }

    if (field.type === 'image-upload' && !value) {
      return true;
    }
    return false;
  });
};
